// src/modules/goldVault/pages/TransferCreate.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Form, Select, Button, Typography, Spin, message, Row, Col,
  Table, Statistic, Divider, Input, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import { createTransfer } from '../api';
import { fetchWalkInCases } from '../../walkInCase/api';
import { fetchPledgeReleaseCases } from '../../pledgeReleaseCase/api';
import { fetchBranches } from '../../branches/api';
import { useAuth } from '../../../context/AuthContext';

const { Title, Text } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

export default function TransferCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();

  const [eligibleCases, setEligibleCases] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const userBranchId = typeof user?.branch === 'object' ? user?.branch?._id : user?.branch;

  useEffect(() => {
    loadEligibleCases();
    fetchBranches({ limit: 200 }).then((r) => {
      const list = Array.isArray(r) ? r : (r.data || []);
      setBranches(list);
    }).catch(() => {});
  }, [userBranchId]);

  const loadEligibleCases = async () => {
    setLoading(true);
    try {
      const branchParam = userBranchId ? { branch: userBranchId } : {};
      const [walkRes, pledgeRes] = await Promise.all([
        fetchWalkInCases({ inventoryStatus: 'IN_BRANCH_VAULT', limit: 200, ...branchParam }),
        fetchPledgeReleaseCases({
          inventoryStatus: 'IN_BRANCH_VAULT', limit: 200, includeGoldReceipt: true, ...branchParam,
        }),
      ]);
      const walk = (walkRes.data || []).map((c) => ({
        _id: c._id, caseId: c.caseId, type: 'WALK_IN',
        customerName: c.customerId?.name || '—',
        grossWeight: c.totalGrossWeight, netWeight: c.totalNetWeight,
        purchaseCost: c.finalPaymentToCustomer || 0,
      }));
      // A pledge case's own totals are only the pre-valuation estimate. The branch vault
      // holds — and the transfer will move — the assayed GoldReceipt weights.
      const pledge = (pledgeRes.data || []).map((c) => ({
        _id: c._id, caseId: c.caseId, type: 'PLEDGE',
        customerName: c.customerId?.name || '—',
        grossWeight: c.goldReceipt?.finalTotalGrossWeight
          || c.finalTotalGrossWeight || c.totalGrossWeight,
        netWeight: c.goldReceipt?.finalTotalNetWeight
          || c.finalTotalNetWeight || c.totalNetWeight,
        purchaseCost: c.finalNetValue || c.approxFinalAmount || 0,
      }));
      setEligibleCases([...walk, ...pledge]);
    } catch (e) {
      messageApi.error('Failed to load eligible cases');
    } finally {
      setLoading(false);
    }
  };

  const selected = eligibleCases.filter((c) => selectedKeys.includes(c._id));
  const totalGross = selected.reduce((s, c) => s + (c.grossWeight || 0), 0);
  const totalNet = selected.reduce((s, c) => s + (c.netWeight || 0), 0);
  const totalCost = selected.reduce((s, c) => s + (c.purchaseCost || 0), 0);

  const handleSubmit = async () => {
    if (!selectedKeys.length) return messageApi.warning('Select at least one case');
    const values = await form.validateFields();
    const walkInCaseIds = selected.filter((c) => c.type === 'WALK_IN').map((c) => c._id);
    const pledgeCaseIds = selected.filter((c) => c.type === 'PLEDGE').map((c) => c._id);

    setSubmitting(true);
    try {
      const res = await createTransfer({
        walkInCaseIds,
        pledgeCaseIds,
        businessDate: values.businessDate?.toISOString(),
        destinationBranch: values.destinationBranch,
        remarks: values.remarks,
      });
      messageApi.success('Transfer created successfully');
      navigate(`/vault/transfers/${res.data._id}`);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const caseColumns = [
    { title: 'Case No.', dataIndex: 'caseId', key: 'caseId', width: 110 },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 80,
      render: (v) => v === 'WALK_IN' ? 'Walk-In' : 'Pledge' },
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName', width: 140 },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'gw', width: 90, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'nw', width: 90, render: fmtW },
  ];

  return (
    <div style={{ padding: 24 }}>
      {ctx}
      <Title level={4}>Create Inventory Transfer</Title>

      <Row gutter={16}>
        <Col xs={24} md={16}>
          <Card title="Select Cases (IN_BRANCH_VAULT)" size="small" style={{ marginBottom: 16 }}>
            <Spin spinning={loading}>
              <Table
                dataSource={eligibleCases}
                rowKey="_id"
                columns={caseColumns}
                size="small"
                scroll={{ x: 600 }}
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: setSelectedKeys,
                }}
                pagination={{ pageSize: 10 }}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Transfer Summary" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={8}>
              <Col span={12}><Statistic title="Cases" value={selected.length} /></Col>
              <Col span={12}><Statistic title="Gross Wt" value={fmtW(totalGross)} /></Col>
              <Col span={12}><Statistic title="Net Wt" value={fmtW(totalNet)} /></Col>
            </Row>
          </Card>

          <Card title="Transfer Details" size="small">
            <Form form={form} layout="vertical">
              <Form.Item label="Business Date" name="businessDate" initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Destination Branch (HO)" name="destinationBranch">
                <Select placeholder="Select HO Branch" allowClear>
                  {branches.map((b) => (
                    <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Remarks" name="remarks">
                <Input.TextArea rows={3} placeholder="Optional remarks" />
              </Form.Item>
              <Button
                type="primary"
                block
                loading={submitting}
                onClick={handleSubmit}
                disabled={!selectedKeys.length}
              >
                Create Transfer
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
